import { ReactNode, useCallback } from 'react';

import {
   LastMedicalTreatmentConsentQuery,
   MedicalTreatmentConsent,
   MedicalTreatmentConsentCreateCommand,
   PatientSimple,
   PatientSimpleQuery,
} from '@dreryk/egabinet-patient-frontend-api';

import { performCqrsQueryFind, performCqrsQueryGet, useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent, ModalWidth, ShowModalEventInterceptor } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { translationKeys } from '../translations';

type MedicalTreatmentConsentEnterEventInterceptorProps = {
   readonly patientId: number;
   readonly children: ReactNode;
};

export const MedicalTreatmentConsentEnterEventInterceptor = (
   props: MedicalTreatmentConsentEnterEventInterceptorProps
): JSX.Element => {
   const { executeAsync: enterConsent } = useCqrsCommand<MedicalTreatmentConsent, void>(
      () => {
         const command = new MedicalTreatmentConsentCreateCommand();
         command.patientId = props.patientId;
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.consentHasBeenEntered);
         },
      }
   );

   const handleShowModal = useCallback(async (): Promise<boolean> => {
      const patient = await performCqrsQueryGet<PatientSimple>(
         () => {
            const query = new PatientSimpleQuery();
            query.id = Slot.of(props.patientId);
            return query;
         },
         {
            cacheTime: 'never',
         }
      );

      const externalIdentifer = patient.referenceIdentifier?.findExternalIdentifier();
      if (!externalIdentifer) {
         return false;
      }

      const consent = await performCqrsQueryFind<MedicalTreatmentConsent | null>(() => {
         const query = new LastMedicalTreatmentConsentQuery();
         query.patientId = props.patientId;
         return query;
      });

      return Boolean(!consent || consent.withdrawalDate);
   }, [props.patientId]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={handleShowModal}
         title={translationKeys.consentForMedicalServices}
         confirmationButtonLabel={translationKeys.yesTheyAgree}
         content={translationKeys.doesPatientConsentToMedicalServicesAndDataProcessing}
         onConfirm={async (resolve, reject) => enterConsent().then(resolve).catch(reject)}
         testId={'medicalTreatmentConsent'}
         modalWidth={ModalWidth.NORMAL}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
